// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.HighmapActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import 'share/utils/Izenda.StringUtilities';
import { MappingKey, ContainerName, DrilldownLevels } from 'logic/data/Izenda.ReportPartMapConstants';
import { toJS } from 'mobx';
import { compose, withProps, branch, renderComponent } from 'recompose';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import ErrorContainer from 'share/components/controls/Izenda.ErrorContainer';
import AppGlobal from 'Izenda.AppGlobal';
import MapStaticContentStore from 'logic/Izenda.MapStaticContentStore';
import { getValueFromMappingField, getColorSettingsRange } from 'share/utils/Izenda.ProcessDataUtils';
import { getFieldValue } from 'share/utils/Izenda.ReportPartUtils';
import { isValidLatLong } from './googlemaps/Izenda.GoogleMapsUtils';

import {
  getOr,
  first,
  get,
  flow,
  last,
  find,
  toLower,
  filter,
  isNil,
  head,
  has,
  findIndex,
  nth,
  isFunction,
  isObject,
  identity,
  isEmpty,
  includes,
  map,
  deburr,
  inRange,
  each,
  curry
} from 'lodash/fp';

const EnhancedErrorContainer = compose(
  withProps(() => {
    return {
      errors: ['Cannot load GEO file']
    };
  })
)(ErrorContainer);

const getPropertyFromDrillInfo = (drillInfo: any, key: string) =>
  get(`point.pointInfo.properties[${key}]`, drillInfo) || get(`point.pointInfo[${key}]`, drillInfo);

/**
 * Build series to send to hightmap
 * @param {any} mapOptions
 * @param {any} mapData
 * @param {any} backgroundMap
 * @param {any} activePointOption
 * @param {any} mappingData
 * @param {Function} fnUpdateMappingData
 */
function getSeriesMap(
  mapOptions: any,
  mapData: any,
  backgroundMap: any,
  activePointOption: any,
  mappingData: any,
  fnUpdateMappingData: Function,
  getGeoPoint: Function
) {
  const dataStructure = getOr({}, 'dataStructure', mapData);
  const fieldsMapping = getOr([], 'fieldsMapping', mapData);
  const backgroundMapFeatures = getOr([], 'features', backgroundMap);

  const markedShadingPoints = [];
  const shadingSeries = {}; // shading map
  const bubbleSeries = {}; // bubble map

  const misMatchedPoint = []; // save points which do not match with map-data
  const activePointOptionType = get('pointType', activePointOption);
  const isUserDefinedPoint = activePointOptionType === ContainerName.LATITUDE;
  const shownRecords = [];

  let mappingKeys,
    firstMappingKey,
    shadingField,
    bubbleFields,
    activePointVal,
    activePointField,
    activePointGEOInfo,
    pointCode,
    records,
    commonBubbleOptions,
    postalCodes;

  postalCodes = toJS(getOr([], 'rawData.postalCodes', mapData));

  if (activePointOption && (activePointField = first(dataStructure[activePointOptionType]))) {
    mappingKeys = MappingKey[activePointOptionType];
    firstMappingKey = first(mappingKeys);

    records = toJS(getOr([], 'rawData.records', mapData));

    const currentDrillDownMapInfo = flow(
      get('reportPartState.drilldownInfo'),
      last,
      get('mapInfo')
    )(mapOptions);
    const countryInfo = get('countryInfo', mapOptions);

    // Should use highmap geo point instead of server's geo point
    let useHighmapGEOPoint = false;

    if (activePointOptionType === ContainerName.COUNTRY) {
      useHighmapGEOPoint = true;
    }

    if (activePointOptionType === ContainerName.STATE) {
      const countryCode = isEmpty(countryInfo)
        ? get('backgroundMap.value', currentDrillDownMapInfo)
        : get('value', countryInfo);
      const currentDrillDownLevel = get('mapLevel', currentDrillDownMapInfo);

      // For drilldown to state level, exclude the other country from records
      if (!isEmpty(dataStructure.country)) {
        const countryColumnName = get(ContainerName.COUNTRY + '[0].columnName', dataStructure);
        const country = find(code => toLower(code.code2) === toLower(countryCode), AppGlobal.countryCodes);

        country &&
          (records = filter(
            record =>
              record[countryColumnName] === country.name ||
              record[countryColumnName] === country.code2 ||
              record[countryColumnName] === country.code3,
            records
          ));
      }

      // For drilldown to country level, exclude the other state from records
      if (!isEmpty(dataStructure.state) && countryCode) {
        const cities = toJS(getOr([], 'rawData.cities', mapData));
        const stateColumnName = get(ContainerName.STATE + '[0].columnName', dataStructure);
        const citiesInDrilldownCountry = filter(city => toLower(city.countryCode) === toLower(countryCode), cities);

        const woeNameList = map('woeName', citiesInDrilldownCountry);
        const hcA2List = map('hcA2', citiesInDrilldownCountry);

        !isEmpty(citiesInDrilldownCountry) &&
          (records = filter(
            record =>
              includes(deburr(record[stateColumnName]), map(deburr, woeNameList)) ||
              includes(record[stateColumnName], hcA2List),
            records
          ));
      }

      // Do not show state data on state level
      if (currentDrillDownLevel === DrilldownLevels.STATE && isEmpty(dataStructure.city)) {
        records = [];
      }

      // Use highmap geo point to make bubble center in state
      countryCode && (useHighmapGEOPoint = true);
    }

    // if active point is county, filter county not belong to state dril
    if (activePointOptionType === ContainerName.COUNTY) {
      const stateColumnName = flow(
        get('state'),
        head,
        get('columnName')
      )(dataStructure);

      const lastDilldownInfo = last(mapOptions.reportPartState.drilldownInfo);
      if (lastDilldownInfo) {
        if (get('mapInfo.mapLevel', lastDilldownInfo) === DrilldownLevels.STATE) {
          useHighmapGEOPoint = true;
        }
        // for case user drildown on map
        records = records.filter(item => {
          const value = item[stateColumnName];
          // if don't have state value, no need to filter
          if (isNil(value)) {
            return true;
          }

          const properties =
            get('point.pointInfo.properties', lastDilldownInfo) || get('point.pointInfo', lastDilldownInfo) || {};

          return isMatchedPoint(value, properties, MappingKey[ContainerName.STATE]);
        });
      } else {
        // for case user select map on properties panel
        // TODO: will be hanlde for another ticket
      }
    }

    if (
      [ContainerName.STATE, ContainerName.POSTAL_CODE, ContainerName.CITY, ContainerName.COUNTY].indexOf(
        activePointOptionType
      ) > -1 &&
      isNil(mappingData)
    ) {
      loadMappingData(
        activePointOptionType,
        mapData,
        getValueFromMappingField(activePointField, records),
        fnUpdateMappingData
      );
      return {};
    } else if (isUserDefinedPoint) {
      commonBubbleOptions = {
        latField: head(dataStructure[ContainerName.LATITUDE]),
        lonField: head(dataStructure[ContainerName.LONGTITUDE]),
        isUserDefinedPoint: isUserDefinedPoint
      };
    }

    // filter city which belongs to post code.
    const lastDrillInfo = last(mapOptions.reportPartState.drilldownInfo);
    const hcA2 = getPropertyFromDrillInfo(lastDrillInfo, 'hc-a2');

    if (ContainerName.POSTAL_CODE === activePointOptionType) {
      let countryValue, name;

      if (!currentDrillDownMapInfo) {
        if (
          [DrilldownLevels.COUNTRY, DrilldownLevels.WORLD, DrilldownLevels.CONTINENT, DrilldownLevels.STATE].indexOf(
            mapOptions.commonOptions.izendaOptions.mapLevel
          ) > -1
        ) {
          countryValue = get('countryInfo.others.BASE_NAME', mapOptions)
            ? get('countryInfo.others.BASE_NAME', mapOptions)
            : get('countryInfo.value', mapOptions);
          name = get('stateInfo.label', mapOptions);
        }
      } else {
        switch (currentDrillDownMapInfo.mapLevel) {
          case DrilldownLevels.COUNTRY:
            countryValue = hcA2;
            break;
          case DrilldownLevels.STATE:
            countryValue = getPropertyFromDrillInfo(lastDrillInfo, 'country');
            name = getPropertyFromDrillInfo(lastDrillInfo, 'name');
            break;
        }
      }

      records = records.filter(record => {
        const activePointVal = getValueFromMappingField(activePointField, record);
        const activePointGEOInfo = flow([get(activePointVal), head])(mappingData);

        if (!activePointGEOInfo || !countryValue) {
          return true;
        }
        const matchedPostcode = find(
          p => toLower(p.postalCode) === toLower(activePointGEOInfo.postalCode),
          postalCodes
        );

        if (!matchedPostcode) {
          return true;
        }

        if (
          toLower(matchedPostcode.countryCode) !== toLower(countryValue) &&
          toLower(matchedPostcode.countryName) !== toLower(countryValue)
        ) {
          return false;
        }
        return name
          ? toLower(matchedPostcode.province) === toLower(name) || toLower(matchedPostcode.province) === toLower(hcA2)
          : true;
      });
    }

    // filter city which belongs to state/province.
    if (ContainerName.CITY === activePointOptionType) {
      let compareValue, level;
      if (!currentDrillDownMapInfo) {
        const country = get('countryInfo.others.BASE_NAME', mapOptions)
          ? get('countryInfo.others.BASE_NAME', mapOptions)
          : get('countryInfo.value', mapOptions);
        const state = get('stateInfo.label', mapOptions);
        if (!!state) {
          level = DrilldownLevels.STATE;
          compareValue = state;
        } else if (!!country) {
          level = DrilldownLevels.COUNTRY;
          compareValue = country;
        } else {
          compareValue = get('continentInfo.value', mapOptions);
          compareValue && (level = DrilldownLevels.CONTINENT);
        }
      } else {
        switch (currentDrillDownMapInfo.mapLevel) {
          case DrilldownLevels.CONTINENT:
            level = DrilldownLevels.CONTINENT;
            compareValue = getPropertyFromDrillInfo(lastDrillInfo, 'continent');
            break;
          case DrilldownLevels.COUNTRY:
            compareValue = hcA2;
            level = DrilldownLevels.COUNTRY;
            break;
          case DrilldownLevels.STATE:
            compareValue = getPropertyFromDrillInfo(lastDrillInfo, 'woe-name');
            level = DrilldownLevels.STATE;
            break;
        }
      }

      records = records.filter(record => {
        const activePointVal = getValueFromMappingField(activePointField, record);
        const activePointGEOInfo = getGeoPoint(get(activePointVal, mappingData), record, dataStructure, activePointOptionType);

        if (!activePointGEOInfo || !compareValue) {
          return true;
        }

        switch (level) {
          case DrilldownLevels.CONTINENT: {
            const matchedCountry = find(
              continent => toLower(get('properties["hc-a2"]', continent)) === toLower(activePointGEOInfo.countryCode),
              backgroundMapFeatures
            );
            return !!matchedCountry;
          }
          case DrilldownLevels.COUNTRY:
            return toLower(compareValue) === toLower(activePointGEOInfo.countryCode);
          case DrilldownLevels.STATE:
            return toLower(compareValue) === toLower(activePointGEOInfo.woeName);
        }

        return true;
      });
    }

    shadingField = first(dataStructure[ContainerName.SHADING_VALUES]);
    bubbleFields = dataStructure[ContainerName.BUBBLE_VALUES];

    // init data
    shadingField && initMapSerieData(shadingSeries, shadingField, activePointOption);
    bubbleFields &&
      bubbleFields.forEach(function(bubbleField) {
        initMapSerieData(bubbleSeries, bubbleField, activePointOption, commonBubbleOptions);
      });

    // loop preview-records & build series
    records.forEach(record => {
      activePointVal = getValueFromMappingField(activePointField, record);

      if (!activePointVal) {
        return true; // break forEach
      }

      if (isUserDefinedPoint) {
        // bubble series - get data from lat/long fields
        if (bubbleFields && addBubbleData(activePointOption, bubbleSeries, null, null, null, record, fieldsMapping)) {
          shownRecords.push(record);
        }
        return true; // break forEach
      }

      activePointGEOInfo = !useHighmapGEOPoint
        ? getGeoPoint(get(activePointVal, mappingData), record, dataStructure, activePointOptionType)
        : getGEOInfo(
            activePointVal,
            mappingKeys,
            backgroundMapFeatures,
            false,
            activePointOptionType
          );

      // mis-matched point
      if (!activePointGEOInfo) {
        misMatchedPoint.push({
          pointName: activePointVal,
          record: record
        });

        return true; // break forEach
      }

      pointCode = get(firstMappingKey.key, activePointGEOInfo.properties);

      let trackShadingRecord = false;
      let trackBubbleRecord = false;
      // shading serie (only one serie)
      if (shadingField) {
        addPoint(
          activePointOption,
          shadingSeries,
          shadingField,
          record,
          pointCode,
          activePointVal,
          activePointGEOInfo,
          fieldsMapping
        );
        trackShadingRecord = true;
      }

      // bubble series
      if (bubbleFields) {
        trackBubbleRecord = addBubbleData(
          activePointOption,
          bubbleSeries,
          activePointVal,
          pointCode,
          activePointGEOInfo,
          record
        );
      }

      (trackShadingRecord || trackBubbleRecord) && shownRecords.push(record);
    });
  }

  return {
    pointOption: activePointOption,
    backgroundMap: {
      joinBy: ['hc-key', 'code'],
      data: getBackgroundPoints(backgroundMapFeatures, markedShadingPoints)
    },
    shadingSeries: shadingSeries,
    bubbleSeries: bubbleSeries,
    pointMap: [],
    misMatchedPoint: misMatchedPoint, // TODO
    shownRecords
  };

  /**
   * get background-map (to support highlight for point which don't have data)
   * @param {Array<any>} backgroundMapFeatures
   * @param {Array<any>} markedShadingPoints
   */
  function getBackgroundPoints(backgroundMapFeatures: Array<any>, markedShadingPoints: Array<any>) {
    const backgroundMapPoints = [];

    for (let i = 0; i < backgroundMapFeatures.length; i++) {
      !markedShadingPoints[i] &&
        backgroundMapPoints.push({
          code: backgroundMapFeatures[i].properties['hc-key'],
          value: 0,
          pointInfo: backgroundMapFeatures[i]
        });
    }
    return backgroundMapPoints;
  }

  /**
   * init map serie data
   * @param {any} source
   * @param {any} field
   * @param {any} activePointOption
   * @param {any} commonOptions
   */
  function initMapSerieData(source: any, field: any, activePointOption: any, commonOptions: any) {
    source[field.fieldNameAlias] = $izenda.extend(true, {}, commonOptions, {
      sum: 0,
      min: Number.MAX_VALUE,
      max: Number.MIN_VALUE,
      data: [],
      field: field,
      activePointOption: activePointOption
    });
  }

  /**
   * get data for bubbleSeries field
   * @param {any} activePointOption
   * @param {any} bubbleSeries
   * @param {string} activePointVal
   * @param {string} pointCode
   * @param {any} activePointGEOInfo
   * @param {any} record
   */
  function addBubbleData(
    activePointOption: any,
    bubbleSeries: any,
    activePointVal: string,
    pointCode: string,
    activePointGEOInfo: any,
    record: any,
    fieldsMapping: any[]
  ) {
    let trackRecord = false;
    bubbleFields.forEach(bubbleField => {
      addPoint(
        activePointOption,
        bubbleSeries,
        bubbleField,
        record,
        pointCode,
        activePointVal,
        activePointGEOInfo,
        fieldsMapping
      );
      trackRecord = true;
    });
    return trackRecord;
  }

  /**
   * Get "value" type color settings (high + low)
   * @param {any} colorSettings color settings object:
   * {
   *  rangePercent: {...},
   *  rangeValue: {},
   *  value: [{
   *    key: 0, // static value
   *    thresholdType: '', // 'static'|'dynamic'
   *    fields: [], // list of selected field models (SettingsByRangeItemFieldModel)
   *  }]
   * }
   * @param {any[]} fieldsMapping mapping for fieldsId -> columnName
   * @param {any} record current data record
   * @param {number} pointVal current point
   * @return {any} min/max values.
   */
  function colorSettingsDynamicRanges(colorSettings, fieldsMapping, record, pointVal): any {
    return getColorSettingsRange(colorSettings, fieldsMapping, record, pointVal);
  }

  /**
   * Add point
   * @param {any} activePointOption
   * @param {any} source
   * @param {any} field
   * @param {any} record
   * @param {any} pointCode
   * @param {any} activePointVal
   * @param {any} activePointGEOInfo
   */
  function addPoint(
    activePointOption: any,
    source: any,
    field: any,
    record: any,
    pointCode: any,
    activePointVal: any,
    activePointGEOInfo: any,
    fieldsMapping: any[]
  ) {
    const pointType = getOr('', 'pointType', activePointOption);

    activePointGEOInfo = get('properties', activePointGEOInfo) || activePointGEOInfo || {};

    const serie = source[field.fieldNameAlias];
    const fieldValue = field.columnName ? getFieldValue(field.columnName, record) : {};
    const pointVal = fieldValue.originalValue || fieldValue.value;

    // get color settings for current map zoom level (contry/state/city/...)
    const currentPointColorSettings = getOr(
      null,
      `reportPartElm.properties.dataFormattings.color.cellColor.${pointType}`,
      field
    );
    // find color ranges for current zoom level
    const rangesObject = colorSettingsDynamicRanges(currentPointColorSettings, fieldsMapping, record, pointVal);

    const pointInfo = {
      originalValue: fieldValue.originalValue,
      value: pointVal,
      rangesObject: rangesObject,
      formatedValue: fieldValue.originalValue,
      pointName: activePointVal,
      pointInfo: activePointGEOInfo,
      valueField: field,
      drillingDownField: first(dataStructure[activePointOption.pointType]),
      nameField: activePointOption,
      record: record
    };

    pointCode && (pointInfo.code = pointCode);

    if (has('latField', serie)) {
      activePointGEOInfo.latitude = getValueFromMappingField(serie.latField, record);
    }

    if (has('lonField', serie)) {
      activePointGEOInfo.longitude = getValueFromMappingField(serie.lonField, record);
    }

    if (activePointGEOInfo.longitude && activePointGEOInfo.latitude) {
      $izenda.extend(pointInfo, {
        lon: Number(activePointGEOInfo.longitude),
        lat: Number(activePointGEOInfo.latitude)
      });

      if (isValidLatLong(activePointGEOInfo.latitude, activePointGEOInfo.longitude)) {
        serie.sum += pointVal;
        serie.min = Math.min(serie.min, pointVal);
        serie.max = Math.max(serie.max, pointVal);
      }
    }

    serie.data.push(pointInfo);
  }

  /**
   * parse data for shading
   * @param {any} val
   * @param {any} mappingKeys
   * @param {any} backgroundMapFeatures
   * @param {boolean} needMarkPoint
   */
  function getGEOInfo(val: any, mappingKeys: any, backgroundMapFeatures: any, needMarkPoint: boolean, drillDownLevel) {
    // Cannot use backgroundMapFeatures.find() here, it does not work in EVO PDF.
    const pointIndex = findIndex(feature => {
      return isMatchedPoint(val, feature.properties, mappingKeys, drillDownLevel);
    }, backgroundMapFeatures);

    needMarkPoint && pointIndex > -1 && markShadingPoint(pointIndex);
    return pointIndex > -1 ? nth(pointIndex, backgroundMapFeatures) : null;
  }

  /**
   * Mark shading point.
   * @param {Number} index
   */
  function markShadingPoint(index: Number) {
    markedShadingPoints[index] = true;
  }

  /**
   * Check matching point
   * @param {string} pointVal
   * @param {any} source
   * @param {Array<any>} mappingKeys
   */
  function isMatchedPoint(pointVal: string, source: any, mappingKeys: Array<any>, drillDownLevel) {
    return !!find(mappingField => {
      // for case county fips return 1001 but in highmap is 01001;
      if (mappingField === 'fips' && drillDownLevel === ContainerName.COUNTY) {
        return source[mappingField] / 1 === pointVal / 1;
      }
      return mappingField.compare(source[mappingField.key], pointVal);
    }, mappingKeys);
  }
}

/**
 * Load mapping data.
 * @param {string} pointType
 * @param {any} mapData
 * @param {any} values
 * @param {Function} callback
 */
function loadMappingData(pointType: string, mapData: any, values: any, callback: Function) {
  let fnName = '';
  let mappingValues = [];

  switch (pointType) {
    case ContainerName.STATE:
      fnName = 'getStateCodes';
      mappingValues = toJS(getOr([], 'stateCodes', mapData));
      break;
    case ContainerName.COUNTY:
      fnName = 'getCountiesGGMap';
      mappingValues = toJS(getOr([], 'countyCodes', mapData));
      break;
    case ContainerName.CITY:
      fnName = 'getCities';
      mappingValues = toJS(getOr([], 'cities', mapData));
      break;
    case ContainerName.POSTAL_CODE:
      fnName = 'getPostalCode';
      mappingValues = toJS(getOr([], 'postalCodes', mapData));
      break;
    default:
    // do nothing
  }

  fnName = MapStaticContentStore[fnName];
  return isFunction(fnName) && fnName(values, mappingValues).then(callback);
}

/**
 * Finf map range
 * @param {Object} mapCom
 */
function findMapRange(mapCom: any) {
  const mapRange = {
    min: [],
    max: []
  };

  each(axis => {
    mapRange.min[axis.isXAxis ? 0 : 1] = axis.min;
    mapRange.max[axis.isXAxis ? 0 : 1] = axis.max;
  }, mapCom.mapInstance.axes);

  return mapRange;
}

/**
 * Filter records based lat/lon
 * @param {Object} mapCom
 * @param {Object} bubbleSeries
 */
function filterRecordsByLatLon(mapCom: any, bubbleSeries: any) {
  const records = [];
  const mapRange = findMapRange(mapCom);
  const checkLat = curry(inRange(mapRange.min[0], mapRange.max[0]));
  const checkLon = curry(inRange(mapRange.min[1], mapRange.max[1]));

  each(serie => {
    each(point => {
      if (has('lat', point) && has('lon', point)) {
        const latlonPoint = mapCom.mapInstance.fromLatLonToPoint(point);
        const isValid = checkLat(latlonPoint.x) && checkLon(latlonPoint.y);
        isValid && records.push(point.record);
      } else {
        records.push(point.record);
      }
    }, serie.data);
  }, bubbleSeries);

  return records;
}

module.exports = compose(
  withProps(props => {
    return getMapBackground();

    function getMapBackground() {
      const backgroundMap = props.backgroundMap;
      const backgroundMapName = get('commonOptions.izendaOptions.backgroundMapName', props.chartOptions);
      const activePointOption = get('commonOptions.izendaOptions.activePointOption', props.chartOptions);

      if (!backgroundMap || backgroundMap.name !== backgroundMapName) {
        MapStaticContentStore.getGEOJson(backgroundMapName)
          .then(backgroundMapData => {
            props.setMapData('backgroundMap', {
              name: backgroundMapName,
              geojson: backgroundMapData
            });
            return null;
          })
          .error(e => props.setMapData('backgroundMap', {}));

        return {
          backgroundMap: null
        };
      }

      return {
        backgroundMap: backgroundMap,
        seriesMap: getSeriesMap(
          props.chartOptions,
          props.chartData,
          backgroundMap.geojson,
          activePointOption,
          props.mappingData,
          onUpdateMappingData,
          props.getGeoPoint
        ),
        filterRecordsByLatLon
      };

      function onUpdateMappingData(mappingData) {
        props.setMapData('mappingData', mappingData);
        return null;
      }
    }
  }),
  /* show | hide spinner */
  branch(
    props =>
      isObject(props.backgroundMap) &&
      isObject(get('geojson', props.backgroundMap)) &&
      has('backgroundMap', props.seriesMap),
    identity,
    renderComponent(ReportPartUtils.renderLazyLoadingBar)
  ),
  branch(props => !isNil(get('geojson', props.backgroundMap)), identity, renderComponent(EnhancedErrorContainer))
);



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.HighmapActions.js